export type PlanTier = "standard" | "premium";
export type LendingEnvironment = "live" | "sandbox";

export interface SettingValue {
  setting_key: string;
  setting_value: string;
}

export interface LendingRules {
  minAmount: number;
  maxAmount: number;
  maxTermMonths: number;
  interestRate: number;
  approvalSlaHours: number;
}

export interface LendingRulesByTier {
  standard: LendingRules;
  premium: LendingRules;
}

export const getLendingEnvironment = (settings: SettingValue[] = []): LendingEnvironment => {
  const value = readSetting(settings, "lending_environment");
  return value === "sandbox" ? "sandbox" : "live";
};

const readSetting = (settings: SettingValue[], key: string) =>
  settings.find((setting) => setting.setting_key === key)?.setting_value;

const readNumber = (settings: SettingValue[], key: string, fallback: number) => {
  const value = readSetting(settings, key);
  if (value === undefined || value === null || value === "") {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizePlanTier = (planTier?: string | null): PlanTier =>
  planTier === "premium" ? "premium" : "standard";

export const getLendingRules = (
  settings: SettingValue[] = [],
  environment: LendingEnvironment = getLendingEnvironment(settings)
): LendingRulesByTier => {
  const prefix = environment === "sandbox" ? "sandbox_" : "";
  const standardMin = readNumber(settings, `${prefix}min_loan_amount`, environment === "sandbox" ? 1000 : 10000);
  const standardMax = readNumber(settings, `${prefix}max_loan_amount`, environment === "sandbox" ? 50000 : 1000000);
  const standardMaxTerm = readNumber(settings, `${prefix}max_loan_term`, environment === "sandbox" ? 12 : 24);
  const standardRate = readNumber(settings, `${prefix}default_interest_rate`, environment === "sandbox" ? 12.0 : 15.0);
  const standardSla = readNumber(settings, `${prefix}standard_approval_sla_hours`, environment === "sandbox" ? 6 : 48);

  const premiumMax = readNumber(
    settings,
    `${prefix}premium_max_loan_amount`,
    environment === "sandbox" ? 100000 : standardMax * 2
  );
  const premiumMaxTerm = readNumber(
    settings,
    `${prefix}premium_max_loan_term`,
    environment === "sandbox" ? 18 : Math.max(standardMaxTerm, 36)
  );
  const premiumRate = readNumber(
    settings,
    `${prefix}premium_interest_rate`,
    environment === "sandbox" ? 8.0 : Math.max(standardRate - 2, 8)
  );
  const premiumSla = readNumber(settings, `${prefix}premium_approval_sla_hours`, environment === "sandbox" ? 1 : 24);

  return {
    standard: {
      minAmount: standardMin,
      maxAmount: standardMax,
      maxTermMonths: standardMaxTerm,
      interestRate: standardRate,
      approvalSlaHours: standardSla,
    },
    premium: {
      minAmount: standardMin,
      maxAmount: premiumMax,
      maxTermMonths: premiumMaxTerm,
      interestRate: premiumRate,
      approvalSlaHours: premiumSla,
    },
  };
};

export const getPlanRules = (planTier: PlanTier, rules: LendingRulesByTier) =>
  planTier === "premium" ? rules.premium : rules.standard;
